import { Button, Input, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Stack, Text, useDisclosure, useToast } from "@chakra-ui/react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { adminUpdateLicenseStatus } from "../../../../api";
import ImageModal from "../../../../components/Modals/ImageModal";
import { colors } from "../../../../styles";
import { ILicense } from "../../../../types";
import { formatDateTimeKor } from "../../../../utils";
import { FieldRow, ImageThumb, ObjectStatus, StatusSelect, ValiditySelect } from "./shared";

interface ILicenseDraft {
    imageIsValid: boolean;
    numberIsValid: boolean;
    status: ObjectStatus;
    rejectedReason: string;
}

interface ILicenseModalProps {
    isOpen: boolean;
    onClose: () => void;
    userId: string;
    license: ILicense;
}

const toDraft = (license: ILicense): ILicenseDraft => ({
    imageIsValid: license.imageIsValid,
    numberIsValid: license.numberIsValid,
    status: license.status,
    rejectedReason: license.rejectedReason ?? "",
});

export default function LicenseModal({ isOpen, onClose, userId, license }: ILicenseModalProps) {
    const toast = useToast();
    const queryClient = useQueryClient();
    const [draft, setDraft] = useState<ILicenseDraft>(() => toDraft(license));
    const { isOpen: isImageOpen, onOpen: onOpenImage, onClose: onCloseImage } = useDisclosure();
    const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);

    useEffect(() => {
        if (isOpen) setDraft(toDraft(license));
    }, [isOpen, license]);

    const isDirty = useMemo(() => {
        const original = toDraft(license);
        return JSON.stringify(original) !== JSON.stringify(draft);
    }, [license, draft]);

    const allValid = draft.imageIsValid && draft.numberIsValid;

    const updateMutation = useMutation({
        mutationFn: adminUpdateLicenseStatus,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["adminGetUsers"] });
            toast({ title: "면허 정보가 저장되었습니다.", status: "success", position: "top", duration: 2000, isClosable: true });
            onClose();
        },
        onError: () => {
            toast({ title: "면허 정보 저장에 실패했습니다.", status: "error", position: "top", duration: 2000, isClosable: true });
        },
    });

    const onSave = () => {
        updateMutation.mutate({
            userId,
            licenseId: license.id,
            status: draft.status,
            rejectedReason: draft.rejectedReason.trim() || null,
            imageIsValid: draft.imageIsValid,
            numberIsValid: draft.numberIsValid,
        });
    };

    const openImage = (url: string) => {
        setSelectedImageUrl(url);
        onOpenImage();
    };

    return (
        <>
            <Modal isOpen={isOpen} onClose={onClose} size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <Text fontSize={20} fontWeight={700} color={colors.black}>약사 정보</Text>
                        <ModalCloseButton />
                    </ModalHeader>
                    <ModalBody>
                        <Stack spacing={1} width="100%">
                            <FieldRow label="면허 번호">
                                <Text fontSize={14}>{license.number || "-"}</Text>
                            </FieldRow>
                            <FieldRow label="면허 번호 유효성">
                                <ValiditySelect
                                    value={draft.numberIsValid}
                                    onChange={(numberIsValid) => setDraft((prev) => ({ ...prev, numberIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="면허증">
                                <ImageThumb url={license.image} alt="license" onClick={openImage} />
                            </FieldRow>
                            <FieldRow label="면허증 유효성">
                                <ValiditySelect
                                    value={draft.imageIsValid}
                                    onChange={(imageIsValid) => setDraft((prev) => ({ ...prev, imageIsValid }))}
                                />
                            </FieldRow>
                            <FieldRow label="상태">
                                <StatusSelect
                                    value={draft.status}
                                    rejectDisabled={allValid}
                                    onChange={(status) => {
                                        if (allValid && status === "REJECTED") return;
                                        setDraft((prev) => ({ ...prev, status }));
                                    }}
                                />
                            </FieldRow>
                            <FieldRow label="거절 사유">
                                <Input
                                    size="sm"
                                    value={draft.rejectedReason}
                                    placeholder="거절 사유"
                                    onChange={(event) => setDraft((prev) => ({ ...prev, rejectedReason: event.target.value }))}
                                />
                            </FieldRow>
                            <FieldRow label="생성일">
                                <Text fontSize={14}>{formatDateTimeKor(String(license.createdAt))}</Text>
                            </FieldRow>
                            <FieldRow label="수정일">
                                <Text fontSize={14}>{formatDateTimeKor(String(license.updatedAt))}</Text>
                            </FieldRow>
                        </Stack>
                    </ModalBody>
                    <ModalFooter>
                        <Button
                            width="100%"
                            backgroundColor={colors.blue}
                            color={colors.white}
                            borderRadius={10}
                            isDisabled={!isDirty}
                            isLoading={updateMutation.isPending}
                            onClick={onSave}
                        >
                            저장
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
            {selectedImageUrl && (
                <ImageModal isOpen={isImageOpen} onClose={onCloseImage} imageUrl={selectedImageUrl} />
            )}
        </>
    );
}
